import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { FunctionComponent, ReactElement } from 'react';

import type { DrawerProps } from '../components/generic/Drawer';

type DresserDrawerProps = Omit<DrawerProps, 'children' | 'isOpen'>;
type Props = {
  locationBasename?: string;
};

type DrawerItem = {
  id: string;
  content: ReactElement;
  drawerProps?: DresserDrawerProps;
};

type DrawerContextState = {
  drawerContents: DrawerItem[];
  widthPercent?: number;
  setWidthPercent: (widthPercent?: number) => void;
  pushDrawer: (content: ReactElement, drawerProps?: DresserDrawerProps) => void;
  replaceDrawer: (content: ReactElement, drawerProps?: DresserDrawerProps) => void;
  popDrawer: () => void;
  clearDrawers: () => void;
};

export const DrawerContext = createContext<DrawerContextState>({} as DrawerContextState);

export const useDresser = () => useContext(DrawerContext);

/**
 * Hook which allows you to simply add a new drawer to the page.
 * @example
 * const MyComponent = () => {
 *   const addToast = useAddToast();
 *
 *   return (
 *     <Button onClick={() => pushDrawer(<MyDrawer />)}>
 *       Add drawer!
 *     </Button>
 *   );
 * };
 */
export const usePushDrawer = (): (content: ReactElement, drawerProps?: DresserDrawerProps) => void => {
  const { pushDrawer } = useContext(DrawerContext);
  return pushDrawer;
};

export const DrawerContextProvider: FunctionComponent<Props> = ({ children, locationBasename }) => {
  const [drawerContents, setDrawerContents] = useState<DrawerItem[]>([]);
  const [widthPercent, setWidthPercent] = useState<number | undefined>(undefined);

  const pushDrawer = useCallback((content: ReactElement, drawerProps?: DresserDrawerProps) => {
    const newItem: DrawerItem = {
      id: `drawer-${Date.now()}`,
      content,
      drawerProps,
    };
    setWidthPercent(undefined);
    setDrawerContents(prev => [newItem, ...prev]);
  }, []);

  const replaceDrawer = useCallback((content: ReactElement, drawerProps?: DresserDrawerProps) => {
    setWidthPercent(undefined);
    setDrawerContents([{
      id: `drawer-${Date.now()}-${Math.random()}`,
      content,
      drawerProps,
    }, ...drawerContents.slice(1)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to fire this when the drawer contents change because it causes an infinite rerender.
  }, []);

  const popDrawer = useCallback(() => {
    setWidthPercent(undefined);
    setDrawerContents(prev => prev.slice(1));
  }, []);

  const clearDrawers = useCallback(() => {
    setWidthPercent(undefined);
    setDrawerContents([]);
  }, []);

  // If the base pathname changes (excluding query params), clear the drawers.
  useEffect(() => {
    if (drawerContents.length > 0) {
      clearDrawers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- It wants us to include drawerContents.length, but we don't want to fire this when the drawer contents change.
  }, [clearDrawers, locationBasename]);

  const contextValue = useMemo((): DrawerContextState => ({
    drawerContents,
    pushDrawer,
    replaceDrawer,
    popDrawer,
    clearDrawers,
    setWidthPercent,
    widthPercent,
  }), [drawerContents, pushDrawer, replaceDrawer, popDrawer, clearDrawers, setWidthPercent, widthPercent]);

  return <DrawerContext.Provider value={contextValue}>{children}</DrawerContext.Provider>;
};
